import React, { useEffect, useRef } from "react";

/**
 * Lightweight, dependency-free confetti burst. Renders a fixed full-screen
 * canvas, fires one celebratory burst on mount, then cleans itself up after a
 * few seconds. Respects prefers-reduced-motion. Self-contained (no CDN — CSP safe).
 */
const COLORS = ["#1F4A38", "#2E9E6B", "#D95A40", "#F1C24B", "#4CB57A", "#7A3EA5"];

export default function Confetti({ duration = 3200, count = 160 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reduce) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf = 0;
    let start = null;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    // Seed particles from two side "cannons" near the top-center.
    const W = canvas.width;
    const parts = Array.from({ length: count }, (_, i) => {
      const fromLeft = i % 2 === 0;
      const angle = (fromLeft ? -1 : 1) * (Math.PI / 4) + (Math.sin(i) * 0.4);
      const speed = 8 + (i % 7);
      return {
        x: fromLeft ? W * 0.15 : W * 0.85,
        y: -20,
        vx: Math.cos(angle) * speed * (fromLeft ? 1 : -1),
        vy: Math.sin(Math.abs(angle)) * speed + 3,
        size: 5 + (i % 5),
        color: COLORS[i % COLORS.length],
        rot: (i * 37) % 360,
        vr: (i % 2 ? 1 : -1) * (4 + (i % 6)),
      };
    });

    const tick = (t) => {
      if (start == null) start = t;
      const elapsed = t - start;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const fade = Math.max(0, 1 - elapsed / duration);
      for (const p of parts) {
        p.vy += 0.22;          // gravity
        p.vx *= 0.99;          // drag
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        ctx.save();
        ctx.globalAlpha = fade;
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rot * Math.PI) / 180);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        ctx.restore();
      }
      if (elapsed < duration) {
        raf = requestAnimationFrame(tick);
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [duration, count]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="fixed inset-0 pointer-events-none z-[60]"
    />
  );
}
